import {
  Button,
  Container,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { MemoryAxis, MemoryStateSummary, Person } from '@nelnela/contracts';
import { api } from '../lib/api';
import {
  asideCells,
  initialPair,
  isPolypersonal,
  objectsPresent,
  OBJECT_LABELS,
  personColumn,
  readout,
  subjectsPresent,
  SUBJECT_LABELS,
  type Cell,
} from '../lib/forms';
import { glyphFor } from '../lib/marks';
import { renderInline } from '../lib/lesson-md';
import { speak, ttsCalmLine } from '../lib/tts-player';

/**
 * F7 item detail (design frame 4c): what the app believes about ONE item, and the two escape
 * hatches. The per-axis strip is the honest version of the glyph — read / listen / produce, each
 * with its retrievability and whether that number was measured or assumed (`provenance`), because
 * an inherited number is a guess and the screen says so.
 */
export function ItemDetail() {
  const { itemId = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['item', itemId], queryFn: () => api.itemGet(itemId) });
  const [speakErr, setSpeakErr] = useState<string | null>(null);
  const suppress = useMutation({
    mutationFn: (on: boolean) => (on ? api.itemSuppress(itemId) : api.itemUnsuppress(itemId)),
    onSuccess: (v) => qc.setQueryData(['item', itemId], v),
  });
  // The dictionary's missing verb (2026-09-03 walkthrough): catalog browse → "I want this word" →
  // nothing to tap. Save was a reading-surface act only. From here it rides the same capture path
  // (M6e § 1: a capture seeds a due review); the first example is the sentence when one exists,
  // because a real context beats the word standing in for itself.
  const savedQ = useQuery({
    queryKey: ['saved-item', itemId],
    queryFn: () => api.savedList({ itemId, limit: 1 }),
  });
  // A fresh save is invisible to the itemId-filtered query — `saved_item.item_id` stays NULL
  // until the learning-steps resolver links it — so "just saved" is remembered locally or the
  // button would keep offering a save the unique index now refuses.
  const [savedNow, setSavedNow] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const view = q.data!;
      const word = view.item.displayForm ?? view.item.lemma;
      const first = view.catalog?.examples?.[0] ?? null;
      return api.savedCreate({
        targetLang: view.item.targetLang,
        baseLang: view.item.baseLang,
        selection: word,
        sentence: first?.text ?? word,
        ...(first?.translation ? { translation: first.translation } : {}),
      });
    },
    onSuccess: () => {
      setSavedNow(true);
      void qc.invalidateQueries({ queryKey: ['saved-item', itemId] });
    },
  });
  const alreadySaved = savedNow || (savedQ.data?.items.length ?? 0) > 0;
  // The likeliest failure is the row already existing (saved before the resolver linked it, so
  // the query above could not see it) — the unique index refuses the duplicate, which is the
  // system working; say "saved" rather than showing an error about a wish already granted.
  const saveNote = save.isError ? 'Probably saved already — check Words → Saved.' : null;

  if (q.isPending) {
    return (
      <Container size={640} py="lg">
        <Skeleton height={28} width="50%" mb={12} />
        <Skeleton height={90} radius={12} />
      </Container>
    );
  }
  if (q.error || !q.data) {
    return (
      <Container size={640} py="lg">
        <Text c="dimmed">That item isn’t here.</Text>
      </Container>
    );
  }
  const v = q.data;
  const title = v.item.displayForm ?? v.item.lemma;
  const unmet = glyphFor(v.memory) === '○';

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <UnstyledButton onClick={() => nav(-1)} style={{ fontSize: 12, color: 'var(--nn-muted)' }}>
          ← back
        </UnstyledButton>
        <Group gap={10} align="center" wrap="nowrap">
          <span style={{ fontSize: 18, color: 'var(--nn-muted)' }}>{glyphFor(v.memory)}</span>
          <Title order={2} lang="ka" style={{ minWidth: 0 }}>
            {title}
          </Title>
          {/* α: "▶ beside the headword (word audio, TTS)". Sky, per the theme's role registry —
              audio controls are never ember. */}
          <UnstyledButton
            onClick={() => {
              void speak(title, { title, subtitle: 'word', key: `word:${v.item.id}` }).catch((e) =>
                setSpeakErr(ttsCalmLine(e)),
              );
            }}
            aria-label={`Play ${title}`}
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: 15,
              display: 'grid',
              placeItems: 'center',
              background: 'color-mix(in srgb, var(--nn-info) 18%, transparent)',
              color: 'var(--nn-info-text)',
              fontSize: 12,
            }}
          >
            ▶
          </UnstyledButton>
        </Group>
        {speakErr && (
          <Text fz={12} c="dimmed">
            {speakErr}
          </Text>
        )}
        <Group gap={6}>
          {/* The one graded fact a dictionary entry is FOR — the list row shows it and until
              2026-09-03 the detail lost it. */}
          {v.item.level != null && <Tag>level {v.item.level}</Tag>}
          {v.item.pos && <Tag>{v.item.pos}</Tag>}
          <Tag>{v.item.kind.replace('_', ' ')}</Tag>
          {v.item.verified && <Tag>catalog</Tag>}
          {v.suppressed && <Tag>not tracked</Tag>}
        </Group>

        {v.catalog?.gloss && <Text fz={15}>{v.catalog.gloss}</Text>}

        {unmet ? (
          /* A never-met dictionary entry used to read like a lab report — three "not observed"
             rows about a word the reader just wants to learn. One quiet line until any axis has
             data; the full strip returns the moment there is something to say. */
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Memory
            </Text>
            <Text fz={13} mt={4} c="var(--nn-muted)">
              Not met yet — this fills in from what you do.
            </Text>
          </Panel>
        ) : (
          <MemoryStrip memory={v.memory} />
        )}

        <Examples examples={v.catalog?.examples ?? null} />

        <Forms catalogEntryId={v.catalog?.id ?? null} lemma={title} />

        {v.lesson && (
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Grammar
            </Text>
            <Text fz={13.5} mt={4}>
              {renderInline(v.lesson.ruleSummary)}
            </Text>
            <Link
              to={`/words/lesson/${v.lesson.id}`}
              style={{ fontSize: 12, color: 'var(--nn-link)' }}
            >
              {v.lesson.title} →
            </Link>
          </Panel>
        )}

        {v.note && (
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Your note
            </Text>
            <Text fz={13.5} mt={4} fs="italic">
              {v.note}
            </Text>
          </Panel>
        )}

        {v.relations.length > 0 && (
          <Panel>
            <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
              Related
            </Text>
            <Group gap={6} mt={6}>
              {v.relations.map((r) => (
                <Link
                  key={r.item.id}
                  to={`/words/item/${r.item.id}`}
                  style={{ fontSize: 12.5, color: 'var(--nn-link)' }}
                  lang="ka"
                >
                  {r.item.displayForm ?? r.item.lemma}
                  <span style={{ color: 'var(--nn-faint)' }}> · {r.kind}</span>
                </Link>
              ))}
            </Group>
          </Panel>
        )}

        <Group gap={8}>
          {/* "Save" per the standing terminology decision; lexemes only — a pattern's surface
              resolves to nothing, and an unresolvable save would be a card with no consequences. */}
          {v.item.kind === 'lexeme' &&
            (alreadySaved ? (
              <Text fz={12.5} c="var(--nn-muted)" style={{ padding: '6px 4px' }}>
                Saved ✓ — it’ll come up for review
              </Text>
            ) : (
              <Button size="xs" loading={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            ))}
          {saveNote && (
            <Text fz={11.5} c="var(--nn-muted)">
              {saveNote}
            </Text>
          )}
          {/* "Stop tracking" demoted off never-met words: the most prominent action on a
              dictionary entry was a negative engine act the reader had no reason to touch. */}
          {(v.suppressed || !unmet) && (
            <Button
              size="xs"
              variant="default"
              loading={suppress.isPending}
              onClick={() => suppress.mutate(!v.suppressed)}
            >
              {v.suppressed ? 'Track this again' : 'Stop tracking this'}
            </Button>
          )}
        </Group>
        <Text fz={11.5} c="dimmed">
          {v.suppressed
            ? 'This one is out of the model: it collects no reviews and is never scheduled.'
            : 'Everything here is derived from what you did — nothing was self-graded unless you said so.'}
        </Text>
      </Stack>
    </Container>
  );
}

/**
 * α surface 2: EXAMPLES · 3 — the Georgian on the reading surface, the translation under it, and
 * provenance as "a 9px mono whisper a reader can ignore and a skeptic can find".
 *
 * The provenance is the point, not decoration. "A line out of a show you watched" and "a sentence
 * a model drafted" are different claims about the same Georgian, and M10-ii's `source` field
 * exists so this screen can say which. A shelf line is evidence; a drafted one is a plausible
 * sentence that passed six mechanical gates and Jeff's eye. Collapsing them would quietly promote
 * the second.
 *
 * `.nn-reading` because these are SENTENCES — the reading surface's own rule, and the same call
 * the card session's backs make.
 *
 * ONE α DETAIL NOT BUILT: "headword-form lifted bold" inside the example. Doing it right needs the
 * lemmatizer — the example carries an INFLECTED form, not the citation form, so a substring match
 * would miss ვნერვიულობდი under ნერვიულობა and would also bold ღამე inside ღამემდე (the trap
 * `locateSurface` exists to avoid, `worker/src/lib/exercises.ts`). Tokenizing lives in the worker,
 * not here. A wrong bold on the one screen whose job is teaching forms is worse than no bold.
 */
function Examples({
  examples,
}: {
  examples: Array<{ text: string; translation: string; source?: string }> | null;
}) {
  if (!examples || examples.length === 0) return null;
  return (
    <Panel>
      <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
        Examples · {examples.length}
      </Text>
      <Stack gap={14} mt={8}>
        {examples.map((ex, i) => (
          <div key={`${ex.text}-${i}`}>
            <div className="nn-reading" lang="ka" style={{ fontSize: 15 }}>
              {ex.text}
            </div>
            <Text fz={13} c="dimmed" mt={3}>
              {ex.translation}
            </Text>
            {ex.source && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 9,
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  letterSpacing: '.06em',
                  color: 'var(--nn-faint)',
                }}
              >
                {ex.source}
              </div>
            )}
          </div>
        ))}
      </Stack>
      {/* CC BY 2.0 FR wants a credit where the sentence renders, not only in the seed file's
          header (M10-HANDOFF § ii-2 "What ii-2 did NOT deliver"). The per-row chip names the
          source; this line carries the licence. */}
      {examples.some((ex) => ex.source === 'tatoeba') && (
        <Text fz={10} c="var(--nn-faint)" mt={10}>
          Sentences marked tatoeba are from the Tatoeba project (CC BY 2.0 FR).
        </Text>
      )}
    </Panel>
  );
}

/**
 * The verb forms panel (α surface 2b), over M10-i's `catalogForms`.
 *
 * RENDERS NOTHING TODAY, and that is correct rather than broken. `catalog_form` ships empty behind
 * the UniMorph gate — forms land only at ≥ 95 % exact against UniMorph `kat`'s 118 native-verified
 * tables — so the route returns null for every entry and this returns null. That is the ROADMAP
 * gate's else-arm ("else no forms panel") as a shipped state. The decisions it will use when the
 * gate opens live in `lib/forms.ts` and are tested there against fixtures, which is the only place
 * any of this is known to work until then.
 *
 * The query only runs when there is a catalog entry to ask about; a learner's own item has none.
 */
function Forms({ catalogEntryId, lemma }: { catalogEntryId: string | null; lemma: string }) {
  const q = useQuery({
    queryKey: ['catalogForms', catalogEntryId],
    queryFn: () => api.catalogForms(catalogEntryId!),
    enabled: catalogEntryId !== null,
  });
  const forms = q.data?.forms ?? null;
  const [pair, setPair] = useState<{ subject: Person; object: Person } | null>(null);
  const chosen = pair ?? (forms ? initialPair(forms) : null);

  if (!forms || forms.length === 0 || !chosen) return null;
  const poly = isPolypersonal(forms);
  const column = personColumn(forms, chosen.subject, chosen.object);
  const aside = asideCells(forms);

  return (
    <Panel>
      <Group gap={8} align="center">
        <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
          Forms
        </Text>
        <Text fz={11} c="dimmed" lang="ka">
          {lemma}
        </Text>
      </Group>

      {/* α: "WHO = six equal chips", pickers PINNED while the screeves scroll. */}
      <div style={{ position: 'sticky', top: 0, background: 'var(--nn-panel)', paddingTop: 8 }}>
        <PickerRow
          caption="WHO"
          people={subjectsPresent(forms)}
          labels={SUBJECT_LABELS}
          value={chosen.subject}
          onPick={(p) => setPair({ subject: p, object: chosen.object })}
        />
        {/* Absent, not disabled, for a verb that takes no object. */}
        {poly && (
          <PickerRow
            caption="→ TO WHOM"
            people={objectsPresent(forms)}
            labels={OBJECT_LABELS}
            value={chosen.object}
            onPick={(p) => setPair({ subject: chosen.subject, object: p })}
          />
        )}
        <Text fz={12} c="dimmed" mt={6} mb={4}>
          {readout(chosen.subject, chosen.object)}
        </Text>
      </div>

      <Stack gap={10} mt={4}>
        {column.map((c) => (
          <FormCell key={`${c.screeve}-${c.form}`} cell={c} />
        ))}
      </Stack>

      {aside.length > 0 && (
        <>
          <Text fz={10} c="dimmed" tt="uppercase" mt={16} style={{ letterSpacing: '.08em' }}>
            Without a person
          </Text>
          <Stack gap={10} mt={6}>
            {aside.map((c) => (
              <FormCell key={`${c.screeve}-${c.form}`} cell={c} />
            ))}
          </Stack>
        </>
      )}
    </Panel>
  );
}

/** α: six equal chips, 40px tall (minHitBox) — custom, because six Georgian labels overflow a
 *  SegmentedControl at 360 px. That overflow is exactly why α rejected the grid. */
function PickerRow({
  caption,
  people,
  labels,
  value,
  onPick,
}: {
  caption: string;
  people: Person[];
  labels: Partial<Record<Person, string>>;
  value: Person;
  onPick: (p: Person) => void;
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          fontSize: 9,
          letterSpacing: '.08em',
          color: 'var(--nn-faint)',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          marginBottom: 4,
        }}
      >
        {caption}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {people.map((p) => (
          <UnstyledButton
            key={p}
            onClick={() => onPick(p)}
            aria-pressed={value === p}
            lang="ka"
            style={{
              minHeight: 40,
              flex: '1 1 52px',
              borderRadius: 8,
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              background:
                value === p
                  ? 'color-mix(in srgb, var(--nn-accent) 13%, transparent)'
                  : 'transparent',
              border: `1px solid ${value === p ? 'color-mix(in srgb, var(--nn-accent) 35%, transparent)' : 'var(--nn-hairline)'}`,
              color: value === p ? 'var(--nn-accent-text)' : 'var(--nn-text)',
            }}
          >
            {labels[p] ?? p}
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
}

/**
 * One screeve's cell.
 *
 * α: "attested = 4px dot + mono whisper, and silence on the rest — never a warning". Corpus
 * silence is weak evidence for a rare form (`freq.py`'s own docstring), so an unattested form gets
 * no mark at all rather than a caution the data cannot support.
 */
function FormCell({ cell }: { cell: Cell }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          letterSpacing: '.08em',
          color: 'var(--nn-faint)',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        }}
      >
        {cell.label}
      </div>
      <Group gap={7} align="baseline" wrap="nowrap">
        <Text fz={16} lang="ka">
          {cell.form}
        </Text>
        {cell.attested && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 9,
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              color: 'var(--nn-faint)',
            }}
          >
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                background: 'var(--nn-faint)',
                display: 'inline-block',
              }}
            />
            attested
          </span>
        )}
      </Group>
    </div>
  );
}

const AXES: MemoryAxis[] = ['read', 'listen', 'produce'];

/** The per-axis memory strip — the number, the stage, and whether it was measured or assumed. */
export function MemoryStrip({ memory }: { memory: MemoryStateSummary | null }) {
  return (
    <Panel>
      <Group justify="space-between" mb={8}>
        <Text fz={11} c="dimmed" tt="uppercase" style={{ letterSpacing: '.07em' }}>
          Memory
        </Text>
        {memory && memory.exposures > 0 && (
          <Text fz={11} c="dimmed">
            met {memory.exposures}×
          </Text>
        )}
      </Group>
      <Stack gap={7}>
        {AXES.map((axis) => {
          const a = memory?.axes[axis];
          return (
            <Group key={axis} justify="space-between" wrap="nowrap" gap={10}>
              <Text fz={12.5} c="dimmed" style={{ width: 62, flexShrink: 0 }}>
                {axis}
              </Text>
              {a ? (
                <>
                  <div
                    style={{
                      flex: 1,
                      height: 5,
                      borderRadius: 3,
                      background: 'var(--nn-hairline)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round(a.retrievability * 100)}%`,
                        height: '100%',
                        background:
                          a.provenance === 'fitted' ? 'var(--nn-accent)' : 'var(--nn-muted)',
                        opacity: a.provenance === 'fitted' ? 1 : 0.6,
                      }}
                    />
                  </div>
                  <Text
                    fz={11.5}
                    style={{ width: 118, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                    c="dimmed"
                  >
                    {Math.round(a.retrievability * 100)}% · {a.stage}
                    {a.provenance !== 'fitted' ? ' ·assumed' : ''}
                  </Text>
                </>
              ) : (
                <Text fz={11.5} c="dimmed" style={{ flex: 1, textAlign: 'right' }}>
                  not observed
                </Text>
              )}
            </Group>
          );
        })}
      </Stack>
    </Panel>
  );
}

export function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 12,
        padding: 14,
      }}
    >
      {children}
    </div>
  );
}

export function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        padding: '2px 7px',
        borderRadius: 5,
        background: 'var(--nn-panel-2)',
        color: 'var(--nn-muted)',
      }}
    >
      {children}
    </span>
  );
}

/** F14: one lesson, opened from the gloss sheet's grammar strip or the grammar index. */
export function LessonScreen() {
  const { lessonId = '' } = useParams();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['lesson', lessonId], queryFn: () => api.lessonGet(lessonId) });
  if (q.isPending) {
    return (
      <Container size={640} py="lg">
        <Skeleton height={26} width="60%" mb={12} />
        <Skeleton height={140} radius={12} />
      </Container>
    );
  }
  if (q.error || !q.data) {
    return (
      <Container size={640} py="lg">
        <Text c="dimmed">That lesson isn’t here yet.</Text>
      </Container>
    );
  }
  const l = q.data;
  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <UnstyledButton onClick={() => nav(-1)} style={{ fontSize: 12, color: 'var(--nn-muted)' }}>
          ← back
        </UnstyledButton>
        <Title order={2}>{l.title}</Title>
        <Text fz={14}>{renderInline(l.ruleSummary)}</Text>
        <Panel>
          <Text fz={13.5} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {renderInline(l.body)}
          </Text>
        </Panel>
        {l.examples.length > 0 && (
          <Panel>
            <Stack gap={8}>
              {l.examples.map((ex, i) => (
                <div key={i}>
                  <Text fz={14.5} lang="ka">
                    {ex.text}
                  </Text>
                  <Text fz={12.5} c="dimmed">
                    {ex.translation}
                  </Text>
                </div>
              ))}
            </Stack>
          </Panel>
        )}
        <Link to={`/words/item/${l.itemId}`} style={{ fontSize: 12, color: 'var(--nn-link)' }}>
          What the app thinks you know about this →
        </Link>
      </Stack>
    </Container>
  );
}

/** A one-line row for the inventory lists — glyph, form, and its state in a word. */
